import {
  assign,
  cloneDeep,
  each,
  every,
  filter,
  findIndex,
  flatten,
  forEach,
  groupBy,
  includes,
  intersection,
  isEmpty,
  isNil,
  keys,
  map,
  mapValues,
  merge,
  pullAllBy,
  some,
  sortBy,
  toArray,
  unionBy,
  values,
} from 'lodash-es';
import * as Redux from 'redux';
import produce from 'immer';

import { Price } from './../api/api.interfaces';
import { FeatureFlags } from '../api/features.service';
import { Subject } from 'rxjs';

/* tslint:disable:no-switch-case-fall-through */
// Needed because tslint gets confused by a block scope on a case where it always returns within that block.

interface Window {
  __REDUX_DEVTOOLS_EXTENSION__?: any;
}

////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Note: To understand this code is to understand how Redux works. So it's really worth taking an hour or so and
// going through the Redux docs (http://redux.js.org/index.html) to get what's being done here.
//
//
////////////////////////////////////////////////////////////////////////////////////////////////////////////////

// The most basic actions are those which go get data from an API and then load it into our data model.
//
// Note: It looked better to use symbols here instead of constants with strings but that has a downside when you hit
// the Chrome Redux debugging tool. It shows the type of each action and really helps to have a string there so you
// can see what each action is.
export const FETCH_PRODUCTS_AND_PRICING = 'Fetch Products And Pricing';
export const LOAD_PRODUCTS = 'Load Products';
export const LOAD_PRICING = 'Load Pricing';
export const FETCH_OFFERS = 'Fetch Offers';
export const LOAD_OFFERS = 'Load Offers';
export const RESET_FILTERS = 'Reset Filters';

// Then we've got those associated with user actions on the page.
export const SELECT_OFFER = 'Select Offer';
export const SET_PRICES = 'Set Prices';
export const SET_FILTERS = 'Set Filters';
export const SET_PACKAGE = 'Set Package';
export const SET_TIER = 'Set Tier';
export const INSERT_TIERS = 'Insert Tiers';
export const REMOVE_TIER = 'Remove Tier';
export const UPDATE_COUNT = 'Update Count';

export const UNPRICED_CELL = 'Unpriced Cell';
export const MISPRICED_CELL = 'Mispriced Cell';
export const HIGHLY_VARIANT_CELL = 'Highly Variant Cell';
export const POSSIBLE_MISPRICED_PRODUCT = 'Mispriced Product?';
export const UNPRICED_PRODUCT = 'Unpriced Product';
export const PRICED_PRODUCT = 'Priced Product';
export const INCUMBENT_PRODUCT = 'Incumbent Product';
export const NONINCUMBENT_PRODUCT = 'NonIncumbent Product';
export const VALID_PRODUCT = 'Valid Product';
export const CURRENT_PRICE_CELL = 'Current Price Cell';
export const DECREASED_PRICE_CELL = 'Decreased Price Cell';
export const INCREASED_PRICE_CELL = 'Increased Price Cell';
export const UNORDERED_PRICE_CELL = 'Unordered Price Cell';
export const UNPRICED_EMPTY_CELL = 'Unpriced Empty Cell';

export const MEETS_FORMULARY_PRICING = 'Meets Formulary Price';
export const DOES_NOT_MEET_FORMULARY_PRICING = "Didn't Meet Formulary Price";

export const UNSPSCS_FILTER = 'unspscs';
export const PRODUCTS_FILTER = 'products';
export const AUTO_CORRECTED_FILTER = 'alerts.autoCorrected';
export const PRODUCT_ALERT_FILTER = 'alerts.products';
export const CURRENT_MARKETSHARE_FILTER = 'alerts.marketshare';
export const FORMULARY_PRICING_FILTER = 'formularyPricing';

// this is basically the object used in the grid rows
interface IProductPricingWithFlags {
  currentPricePaidByBuyer?: any;
  newPrices?: any;
  incumbentProduct?: any;
  numberOfCrossReferences?: number;
  id?: number;
  productId?: number;
  prices?: Price[];
  pricePaid?: any;
  productFlags?: any;
  uom?: any;
  unitsInPackage?: any;
  flags?: any;
  adjustedPrices?: any;
}

export class OfferPricingModel {
  featureList: FeatureFlags;
  isPriceAutoFillFlag:boolean = true;

  requestId: number;
  sellerId: number;
  offerId: number; // This is the currently selected offer. There may be many offers associated with this RFP.

  initialState = {
    fetching: {
      products: false,
      pricing: false,
    },

    counts: {
      currentPriceCells: 0,
      decreasedPriceCells: 0,
      doesNotMeetFormularyPricing: 0,
      increasedPriceCells: 0,
      incumbentProducts: 0,
      meetsFormularyPricing: 0,
      mispricedCells: 0,
      mispricedProducts: 0,
      nonincumbentProducts: 0,
      products: 0,
      productsByUnspsc: {},
      searchTermMatches: 0,
      unpricedCells: 0,
      unpricedProducts: 0,
      validProducts: 0,
      visibleProducts: 0,
      visibleUnspscs: 0,
    },

    filters: {
      flags: [],
      searchTerm: '',
    },

    products: [],
    offers: [],
  };

  store: any;

  cachedWijmoData: wijmo.collections.ObservableArray;

  ////////////////////////////////////////////////////////////////////////////////
  // Reducers - Again, it's worth reading through the Redux docs to understand why
  // these are structured as they are.
  ////////////////////////////////////////////////////////////////////////////////

  offerPricingReducer(state: any, action: any) {
    if (!state) {
      state = cloneDeep(this.initialState);
    }
    let newState = state;

    switch (action.type) {
      case FETCH_PRODUCTS_AND_PRICING:
        // Mark that we're in the process of fetching. Other code could use this to throw up a loading
        // indicator.
        return produce(state, (draft) => {
          draft.fetching.products = true;
          draft.fetching.pricing = true;
        });

      case LOAD_PRODUCTS:
        return produce(state, (draft) => {
          // Sort the incoming list of products.
          action.data = sortBy(action.data, ['catalogNumber']);

          // By turning off Wijmo's automatic updates, we prevent a significant slowdown as we update the data
          // structure from which our grid pulls.
          this.cachedWijmoData.beginUpdate();

          // Wijmo can use the product data as-is for the first three columns and incumbency.
          // TODO: Remove this from here. The reducer should not be updating the Wijmo cache. It should happen after
          // the state is updated and outside the reducer.
          if (this.cachedWijmoData.length) {
            // empty out the array in case it had previously been set
            this.cachedWijmoData.clear();
          }

          this.cachedWijmoData.push(
            ...map(action.data, (product: IProductPricingWithFlags) => {
              return assign({}, product, {
                crossed: product.numberOfCrossReferences > 0,
              });
            }),
          );

          this.cachedWijmoData.endUpdate();

          // Create a new state based upon the returned rebates information and mark that we're not fetching rebates
          // anymore.
          draft.fetching.products = false;
          draft.fetching.pricing = false;

          draft.products = action.data;
          draft.counts.products = action.data.length;
        });

      case LOAD_OFFERS:
        return produce(state, (draft) => {
          let offers = forEach(action.data, (offer: any) => {
            // SDT-493 - Previously we tried to figure out what the market share tier was. Now we actually
            // receive it from the back-end. As a temporary stop-gap, I'm copying the value to its old name
            // so the rest of the code does not have to change at the moment.
            offer.marketShareTier = offer.currentTierId;

            offer.pricingLoaded = false;

            return offer;
          });

          draft.offers = offers;
        });

      case SET_FILTERS:
        return produce(state, (draft) => {
          if (this.currentOffer(state).pricingLoaded) {
            let {
              flags, // this is usually the list options from a single menu of filter options unless Clear All was clicked
              searchTerm,
            } = action.data;

            // merge the existing selections with the flags passed in
            let newSelections = filter(flags, 'isSelected');
            let deselected = filter(flags, { isSelected: false });
            let currentSelections = draft.filters.flags;

            // the next two actions, union and pullAll, could be chained but kept them separate for clarity
            // get unique values for combined new selections and existing selections
            let combinedUniqueOptions = unionBy(
              newSelections,
              currentSelections,
              'filterId',
            );

            // remove all values that have been deselected
            let selectedFlags = pullAllBy(
              combinedUniqueOptions,
              deselected,
              'filterId',
            );

            draft.filters.flags = selectedFlags;
            draft.filters.searchTerm = searchTerm;
          }
        });

      case RESET_FILTERS:
        return produce(state, (draft) => {
          draft.filters.flags = [];
          draft.filters.searchTerm = '';
        });

      case LOAD_PRICING:
        return produce(state, (draft) => {
          // Sort the incoming list of products so they match the product info we already have.
          action.data.pricing = sortBy(action.data.pricing, ['catalogNumber']);

          // By turning off Wijmo's automatic updates, we prevent a significant slowdown as we update the data
          // structure from which our grid pulls.
          this.cachedWijmoData.beginUpdate();

          // Trim off anything which isn't just the product ID, it's UOM, and prices. Then generate flag and
          // adjustments for every price.
          let pricing: any = map(
            action.data.pricing,
            (product: IProductPricingWithFlags, index) => {
              let pricePaid = product.currentPricePaidByBuyer;

              // What we're trying to get are the "provided" values, that is, the ones somebody actually entered or pasted into
              // the app. The other value provided at each tier is the corrected value and is used for the back-end in the
              // downloads and micro-charts.
              product.prices = mapValues(product.newPrices, (value) => {
                return value.providedPackagePrice;
              });
              product.adjustedPrices = mapValues(product.newPrices, (value) => {
                return value.packagePrice;
              });

              // We're passing a special flag in the last spot to tell the prices and flags that we
              // want to keep the existing adjusted prices.
              let { adjustedPrices, flags, productFlags } =
                this._pricesAndFlags(
                  product,
                  pricePaid,
                  this.tiers(state),
                  state,
                  false,
                );

              // Update the Wijmo data with the prices.
              // TODO: Remove this from here. The reducer should not be updating the Wijmo cache. It should happen after
              // the state is updated and outside the reducer.
              (this.cachedWijmoData as any).setAt(
                index,
                assign({}, this.cachedWijmoData[index], adjustedPrices, {
                  flags,
                  productFlags,
                }),
              );

              return {
                id: product.id,
                incumbentProduct: product.incumbentProduct,
                prices: product.prices,
                pricePaid,
                productFlags,
                uom: product.uom,
                unitsInPackage: product.unitsInPackage,
                flags,
                adjustedPrices,
              };
            },
          );

          this.cachedWijmoData.endUpdate();

          // Attach the prices to the offer.
          draft.fetching.pricing = false;

          draft.offers[this.currentOfferIndex(state)].prices = pricing;
          draft.offers[this.currentOfferIndex(state)].pricingLoaded = true;
        });

      case SELECT_OFFER:
        // The model itself doesn't change, only the view of the data needs to be updated. Again, another argument
        // for not dealing with Wijmo here.
        if (state.offers[this.currentOfferIndex(state)].pricingLoaded) {
          this.updateWijmoProductData(state);
        }

        return state;

      case SET_TIER:
        return produce(state, (draft) => {
          // Get the market share for the tiers above and below this one, if any. Use them to determine whether
          // this change is valid or not.
          let tiers = this.tiers(draft);
          let tierIndex = findIndex(tiers, { id: action.data.id });

          tiers[tierIndex].marketShare = action.data.marketShare;
          tiers[tierIndex].name = action.data.name;

          // Valid or invalid, find the correct tier on the current offer and update its values.
          // TODO: Remove the above comment? I don't think we're allowing invalid tiers any more.
          let offer = draft.offers[this.currentOfferIndex(state)];
          let targetTier = offer.tiers[tierIndex];
          targetTier.marketShare = action.data.marketShare;
          targetTier.name = action.data.name;

          // Evaluate all the tiers and see if any of them are invalid based upon the one before or after them.
          tiers.forEach((tier, index) => {
            let lowerBound = -1;
            let upperBound = Number.MAX_VALUE;

            if (index > 0) {
              lowerBound = tiers[index - 1].marketShare;
            }

            if (index < tiers.length - 1) {
              upperBound = tiers[index + 1].marketShare;
            }

            tier.invalid = !(
              lowerBound < tier.marketShare && tier.marketShare < upperBound
            );

            draft.offers[this.currentOfferIndex(state)].tiers[index].invalid =
              tier.invalid;
          });

          // If we have any invalid, indicate we have unsaved changes. Otherwise, persist the change via an API call.
          let invalidTiers = some(tiers, (tier) => tier.invalid);
          draft.offers[this.currentOfferIndex(state)].unsaved = invalidTiers;

          if (!invalidTiers) {
            // Strip off the "invalid" data because the back-end will freak out when it sees that.
            let barebonesTiers = tiers.map((tier) => {
              return Object.assign(
                {},
                {
                  id: tier.id,
                  name: tier.name,
                  order: tier.order,
                },
                offer.spendLevelType.name === 'UNIT_MARKET_SHARE'
                  ? { unitMarketShare: tier.marketShare }
                  : { dollarMarketShare: tier.marketShare },
              );
            });

            this.api.offers.updateTiers(
              this.offerId,
              barebonesTiers,
              offer.spendLevelType,
            );

            if (this.currentOffer(state).marketShareTier !== null) {
              this.resetMarketShare(draft, tiers);
            }
          }
        });

      case INSERT_TIERS:
        newState = produce(state, (draft) => {
          let offerTiers = this.tiers(draft);

          // Add on the additional tiers and then sort them.
          let newTiers = action.data.tiers;
          let newOfferTiers = offerTiers
            .concat(newTiers)
            .sort((a, b) => a.marketShare - b.marketShare)
            .map((tier, index) => {
              tier.order = index;
              return tier;
            });

          draft.offers[this.currentOfferIndex(state)].tiers = newOfferTiers;

          // Recalculate all the products because, the additional tiers need to have values.
          // Loop through all the changes in the change list. There might just be one product with one price, one
          // product with multiple prices (for example during the delete of all prices for a product), or multiple
          // price changes across multiple products.
          let newPrices = draft.offers[
            this.currentOfferIndex(state)
          ].prices.map((productPricing) => {
            let offerForProduct = productPricing;
            let { adjustedPrices, flags, productFlags } = this._pricesAndFlags(
              offerForProduct,
              offerForProduct.pricePaid,
              this.tiers(draft),
              state,
            );

            offerForProduct.adjustedPrices = adjustedPrices;
            offerForProduct.flags = flags;
            offerForProduct.productFlags = productFlags;

            return offerForProduct;
          });

          // Put the new prices into the state.
          draft.offers[this.currentOfferIndex(state)].prices = newPrices;

          if (this.currentOffer(draft).marketShareTier !== null) {
            this.resetMarketShare(draft, newOfferTiers);
          }
        });

        // TODO: Remove this from here. The reducer should not be updating the Wijmo cache. It should happen after
        // the state is updated and outside the reducer.
        this.updateWijmoProductData(newState);
        return newState;

      case REMOVE_TIER:
        return produce(state, (draft) => {
          // Remove the specified tier from our available tiers.
          let tierIndex = findIndex(this.tiers(state), {
            id: action.data.id,
          });

          draft.offers[this.currentOfferIndex(state)].tiers.splice(
            tierIndex,
            1,
          );

          // Go through all the prices for the current offer and remove the tier pricing from each one.
          let newPrices = draft.offers[
            this.currentOfferIndex(state)
          ].prices.map((productPricing) => {
            delete productPricing.flags[action.data.id];
            delete productPricing.prices[action.data.id];
            delete productPricing.adjustedPrices[action.data.id];

            return productPricing;
          });

          draft.offers[this.currentOfferIndex(state)].prices = newPrices;
        });

      case SET_PACKAGE:
        return produce(state, (draft) => {
          this.cachedWijmoData.beginUpdate();

          let productIndex = this.productIndex(action.data.productId, state);

          let productPrices =
            draft.offers[this.currentOfferIndex(state)].prices[productIndex];
          productPrices.uom = action.data.uom;
          productPrices.unitsInPackage = action.data.unitsInPackage;

          let offerForProduct =
            draft.offers[this.currentOfferIndex(state)].prices[productIndex];

          let { adjustedPrices, flags, productFlags } = this._pricesAndFlags(
            offerForProduct,
            offerForProduct.pricePaid,
            this.tiers(state),
            state,
          );

          productPrices.flags = flags;
          productPrices.productFlags = productFlags;
          productPrices.adjustedPrices = adjustedPrices;

          this.cachedWijmoData.setAt(
            productIndex,
            assign({}, this.cachedWijmoData[productIndex], adjustedPrices, {
              flags,
              productFlags,
              uom: action.data.uom,
              unitsInPackage: action.data.unitsInPackage,
            }),
          );

          this.cachedWijmoData.endUpdate();
        });

      case SET_PRICES:
        return produce(state, (draft) => {
          // Loop through all the changes in the change list. There might just be one product with one price, one
          // product with multiple prices (for example during the delete of all prices for a product), or multiple
          // price changes across multiple products.
          this.cachedWijmoData.beginUpdate();

          each(action.data, (change: any) => {
            let productIndex = this.productIndex(change.productId, state);
            let offerForProduct =
              draft.offers[this.currentOfferIndex(state)].prices[productIndex];

            each(
              change.prices,
              (newPrice: any) =>
                (offerForProduct.prices[newPrice.tierId] =
                  newPrice.packagePrice),
            );

            let { adjustedPrices, flags, productFlags } = this._pricesAndFlags(
              offerForProduct,
              offerForProduct.pricePaid,
              this.tiers(state),
              state,
            );

            let changedProduct =
              draft.offers[this.currentOfferIndex(state)].prices[productIndex];
            changedProduct.flags = flags;
            changedProduct.productFlags = productFlags;
            changedProduct.adjustedPrices = adjustedPrices;
            changedProduct.prices = offerForProduct.prices;

            // TODO: Remove this from here. The reducer should not be updating the Wijmo cache. It should happen after
            // the state is updated and outside the reducer.
            this.cachedWijmoData.setAt(
              productIndex,
              assign(
                {},
                this.cachedWijmoData[productIndex],
                changedProduct,
                adjustedPrices,
                {
                  flags,
                  productFlags,
                  crossed: changedProduct.numberOfCrossReferences > 0,
                  uom: offerForProduct.uom,
                  unitsInPackage: offerForProduct.unitsInPackage,
                },
              ),
            );
          });

          this.cachedWijmoData.endUpdate();
        });

      case UPDATE_COUNT:
        newState = produce(state, (draft) => {
          let filterGroupId = '';
          let flags = draft.filters.flags;

          if (flags.length === 1) {
            filterGroupId = flags[0].filterGroupId;
          }

          if (draft.offers[this.currentOfferIndex(draft)].pricingLoaded) {
            this._updateCounts(draft, filterGroupId);
          }
        });

        this.updateWijmoProductData(newState);
        return newState;

      default:
        // Nothing changed, return the same state.
        return state;
    }
  }

  setIsPriceAutoFillFlag(flag:boolean) {
    this.isPriceAutoFillFlag = flag;
  }

  _cellFlagsAutoFill(
    price: number,
    adjustedPrice: number,
    pricePaid: number,
    isMarketShare: boolean,
    unitsInPackage: number = 1,
    isUnordredPrice: boolean = false,
    isUnpricedEmptyTiers: boolean = false,
  ) {
    let flags = [];

    
    if (price === null && adjustedPrice === null && !this.isPriceAutoFillFlag && isUnpricedEmptyTiers) {
      flags.push(UNPRICED_EMPTY_CELL);
    } else if (price === null && price !== adjustedPrice) {
      flags.push(UNPRICED_CELL);
    } else if (isUnordredPrice) {
      flags.push(UNORDERED_PRICE_CELL);
    } else if (price !== adjustedPrice) {
      flags.push(MISPRICED_CELL);
    }

    // per our data - a single unit is also considered as a package e.g. EA
    // so this calculation works for determining price at the unit level as well as package level
    const roundedPricePaid = Math.round(pricePaid * 100000) / 100000;//only 5 digits
    let packagePrice = Math.round((roundedPricePaid * unitsInPackage) * 100000) / 100000//only 5 digits

    if (
      pricePaid !== null &&
      adjustedPrice !== null &&
      (adjustedPrice < packagePrice - packagePrice * 0.5 ||
        adjustedPrice > packagePrice + packagePrice * 0.5)
    ) {
      flags.push(HIGHLY_VARIANT_CELL);
    }

    if (adjustedPrice !== null && packagePrice && isMarketShare) {
      if (adjustedPrice === packagePrice) {
        flags.push(CURRENT_PRICE_CELL);
      } else if (adjustedPrice < packagePrice) {
        flags.push(DECREASED_PRICE_CELL);
      } else {
        flags.push(INCREASED_PRICE_CELL);
      }
    }

    return flags;
  }

  _cellFlags(
    price: number,
    adjustedPrice: number,
    pricePaid: number,
    isMarketShare: boolean,
    unitsInPackage: number = 1
  ) {
    let flags = [];

    
    if (price === null && price !== adjustedPrice) {
      flags.push(UNPRICED_CELL);
    } else if (price !== adjustedPrice) {
      flags.push(MISPRICED_CELL);
    }

    // per our data - a single unit is also considered as a package e.g. EA
    // so this calculation works for determining price at the unit level as well as package level
    const roundedPricePaid = Math.round(pricePaid * 100000) / 100000;//only 5 digits
    let packagePrice = Math.round((roundedPricePaid * unitsInPackage) * 100000) / 100000//only 5 digits

    if (
      pricePaid !== null &&
      adjustedPrice !== null &&
      (adjustedPrice < packagePrice - packagePrice * 0.5 ||
        adjustedPrice > packagePrice + packagePrice * 0.5)
    ) {
      flags.push(HIGHLY_VARIANT_CELL);
    }

    if (adjustedPrice !== null && packagePrice && isMarketShare) {
      if (adjustedPrice === packagePrice) {
        flags.push(CURRENT_PRICE_CELL);
      } else if (adjustedPrice < packagePrice) {
        flags.push(DECREASED_PRICE_CELL);
      } else {
        flags.push(INCREASED_PRICE_CELL);
      }
    }

    return flags;
  }

  _pricesAndFlags(
    product: IProductPricingWithFlags,
    pricePaid: number,
    tiers: any[],
    state: any,
    keepAdjustedPrices: boolean = false,
  ) {
    let prices = product.prices;
    let adjusted = product.adjustedPrices;
    let incumbentProduct = product.incumbentProduct;

    let adjustedPrices = {};
    let flags = {};
    let productFlags = [];
    let pricedTiers = 0;
    let unpricedTiers = 0;
    let unpricedEmptyTiers = 0;
    let isValidProduct = true;
    let isUnordredPrice: boolean = false;
    let isUnordredPriceCount = 0;
    let isAllPriceNull = false;

    if (!this.isPriceAutoFillFlag) {
      isAllPriceNull = every(tiers, (tier, index) => {
        return isNil(prices[tier.id])
      });
    }

    each(tiers, (tier, index) => {
      // Get the price at this tier.
      let price = prices[tier.id] === undefined ? null : prices[tier.id];
      let adjustedPrice = null;

      if (keepAdjustedPrices) {
        if (!this.isPriceAutoFillFlag) {
          adjustedPrice = price || null;
        } else {
          adjustedPrice =
          adjusted[tier.id] === undefined ? null : adjusted[tier.id];
        }
      } else {
        if (isNil(price)) {
          // If the price is null, take the last adjusted price we had (which will be at a lower tier) and use that as
          // our price. If there is no price (because we're at the lowest tier or there were no previous prices), use
          // the next price at a higher tier (if any).
          if (index === 0) {
            // We're going to have to search for a price at a higher tier.
            if (!this.isPriceAutoFillFlag) {
              adjustedPrice = price;
            } else {
              for (let i = index + 1; i < tiers.length; i++) {
                if (!isNil(prices[tiers[i].id])) {
                  adjustedPrice = prices[tiers[i].id];
                  break;
                }
              }
            }
          } else {
            adjustedPrice = !this.isPriceAutoFillFlag && isNil(price) ? null : adjustedPrices[tiers[index - 1].id];
          }
        } else {
          adjustedPrice = price;
        }
      }

      if (isNil(adjustedPrice)) {
        if (!this.isPriceAutoFillFlag && product.uom && !isAllPriceNull) {
          unpricedEmptyTiers++;
        } else {
          unpricedTiers++;
        }
      } else {
        pricedTiers++;
      }

      isUnordredPrice = index !==0 && price > adjustedPrices[tiers[index - 1].id];
      if (isUnordredPrice) {
        isUnordredPriceCount++;
      }

      adjustedPrices[tier.id] = adjustedPrice;

      flags[tier.id] = this._cellFlagsAutoFill(
        price as any,
        adjustedPrice,
        pricePaid,
        this.isMarketShare(tier.id, state),
        product.unitsInPackage,
        isUnordredPrice,
        unpricedEmptyTiers > 0
      );
    });

    // Look at the flags for every tier. If all of the tiers are marked as highly variant we have a product which may
    // be mispriced.
    if (
      every(flags, (flagsAtTier: any) =>
        includes(flagsAtTier, HIGHLY_VARIANT_CELL),
      )
    ) {
      isValidProduct = false;
      productFlags.push(POSSIBLE_MISPRICED_PRODUCT);
    }

    // If all tiers for this product are missing prices then flag the product as unpriced.
    if (unpricedTiers === tiers.length) {
      isValidProduct = false;
      productFlags.push(UNPRICED_PRODUCT);
    }

    if (unpricedEmptyTiers === tiers.length) {
      isValidProduct = false;
      productFlags.push(UNPRICED_PRODUCT);
    }

    if (
      some(
        flags as [unknown],
        (flagsAtTier: any) =>
          intersection(flagsAtTier, [UNPRICED_CELL, UNPRICED_EMPTY_CELL, MISPRICED_CELL]).length,
      )
    ) {
      isValidProduct = false;
    }

    if (isValidProduct) {
      productFlags.push(VALID_PRODUCT);
    }

    incumbentProduct
      ? productFlags.push(INCUMBENT_PRODUCT)
      : productFlags.push(NONINCUMBENT_PRODUCT);

    if (pricedTiers > 0 && unpricedTiers === 0 && unpricedEmptyTiers === 0) {
      productFlags.push(PRICED_PRODUCT);
    }

    if (isUnordredPriceCount > 0) {
      productFlags.push(UNORDERED_PRICE_CELL);
    }

    if (unpricedEmptyTiers > 0) {
      productFlags.push(UNPRICED_EMPTY_CELL);
    }

    // The product object passed in params is sometimes partial.
    const storedProduct = this.product(product.id, state);
    const providedTierPrice = (prices[tiers[0].id] || 0) as number;
    const targetPrice =
      storedProduct.unitsInPackage * storedProduct.formularyPrice;

    if (
      storedProduct.uom &&
      storedProduct.formularyPrice &&
      providedTierPrice
    ) {
      if (targetPrice < providedTierPrice) {
        productFlags.push(DOES_NOT_MEET_FORMULARY_PRICING);
      } else if (targetPrice && providedTierPrice) {
        productFlags.push(MEETS_FORMULARY_PRICING);
      }
    }

    // A product is marked priced if it has no pricing at all or if it has a price at every tier. Anything in between
    // does not count as priced for the purposes of trying to figure out if our offer is priced.
    return {
      adjustedPrices,
      flags,
      productFlags,
    };
  }

  /**
   *
   * Go through all the products on the current offer and get update all the counts based upon the
   * flags set.
   *
   * TODO: breakup into two Angular filters for simple unit tests
   */
  _updateCounts(state: any, filterGroupId?: string) {
    // console.time('_updateCounts');
    // clone the initial counts object - all values zeroed out
    let initCounts = cloneDeep(this.initialState.counts);

    let offerPriced = false;

    let selectedFiltersByGroupId =
      groupBy(state.filters.flags, 'filterGroupId') || {};

    let searchTerm = state.filters.searchTerm;

    const searchTermRegex = searchTerm.replace(/\\/g, '/');
    const searchTermMatcher = new RegExp(`(${searchTermRegex})`, 'gi');

    let selectedUnspscFilterFlags =
      map(selectedFiltersByGroupId[UNSPSCS_FILTER], 'filterId') || [];
    let singleFilterGroupIdSelected = '';

    // if there is only one filterGroup then track it as singleFilterGroupIdSelected
    if (keys(selectedFiltersByGroupId).length === 1 && !searchTerm) {
      singleFilterGroupIdSelected = (
        toArray(selectedFiltersByGroupId)[0][0] as any
      ).filterGroupId;
    }

    // if the filterGroup does not have any selected items then treat this as if 'clear'
    // was clicked on that filter group - e.g user manually deselects each items from a dropdown as apposed to using clear
    if (!selectedFiltersByGroupId[filterGroupId]) {
      filterGroupId = '';
    }

    this.cachedWijmoData.beginUpdate();

    // BUG-1322 UNSPSC count not updating
    let visibleUnspscs = new Set();

    let counts = state.offers[this.currentOfferIndex(state)].prices.reduce(
      (counts, product, index) => {
        let masterProduct = state.products[index];

        let productFlags = [].concat(
          product.productFlags,
          flatten(values(product.flags)),
          masterProduct.unspsc.id,
        );

        let productUnspscId = masterProduct.unspsc.id;
        let productSearchTermMatchCount = 0;

        counts.productsByUnspsc[productUnspscId] =
          counts.productsByUnspsc[productUnspscId] || 0;

        if (includes(productFlags, PRICED_PRODUCT)) {
          offerPriced = true;
        }

        if (!isEmpty(selectedFiltersByGroupId)) {
          /*
           * with each product go through all the selectedFiltersByGroupId
           * every group filter should return a match
           * an intersection of flags and productFlags for each filterGroup
           * indicates that at least one match was made for that filerGroup
           */
          product.isVisible = every(selectedFiltersByGroupId, (filters) => {
            filters = map(filters, 'filterId');
            let intersections = intersection(filters, productFlags);
            return !isEmpty(intersections);
          });
        } else {
          product.isVisible = true;
        }

        // set isVisible based on searchTerm
        if (searchTerm.length && product.isVisible) {
          productSearchTermMatchCount += (
            masterProduct.catalogNumber
              .toLowerCase()
              .match(searchTermMatcher) || []
          ).length;
          productSearchTermMatchCount += (
            masterProduct.description.toLowerCase().match(searchTermMatcher) ||
            []
          ).length;

          counts.searchTermMatches += productSearchTermMatchCount;
          product.isVisible = productSearchTermMatchCount > 0;
        }

        let hasUnpricedCells = false;
        let hasMispricedCells = false; // not sure if a mispriced cell should count towards a potentially mispriced product

        /*
         * AUTO_CORRECTED_FILTER
         */
        // auto-corrected flags are at the tier level so we have to count all occurrences per product per cell
        each(product.flags, (tier) => {
          // aggregate counts across all tiers
          forEach(tier, (flag) => {
            if (
              (product.isVisible && filterGroupId !== AUTO_CORRECTED_FILTER) ||
              singleFilterGroupIdSelected === AUTO_CORRECTED_FILTER
            ) {
              switch (flag) {
                case UNPRICED_CELL:
                  hasUnpricedCells = true;
                  counts.unpricedCells++;
                  break;
                case MISPRICED_CELL:
                  hasMispricedCells = true;
                  counts.mispricedCells++; // there should only ever be one per row
                  break;
              }
            } else if (filterGroupId === AUTO_CORRECTED_FILTER) {
              if (flag === UNPRICED_CELL) {
                counts.unpricedCells = state.counts.unpricedCells;
              }

              if (flag === MISPRICED_CELL) {
                counts.mispricedCells = state.counts.mispricedCells;
              }
            }
          });
        });

        /*
         * CURRENT_MARKETSHARE_FILTER
         */
        // this pertains to the marketShare cell in the marketShare column for each product row
        if (
          (product.isVisible && filterGroupId !== CURRENT_MARKETSHARE_FILTER) ||
          singleFilterGroupIdSelected === CURRENT_MARKETSHARE_FILTER
        ) {
          productFlags.forEach((flag) => {
            switch (flag) {
              case DECREASED_PRICE_CELL:
                counts.decreasedPriceCells++; // there should only ever be one per row
                break;
              case INCREASED_PRICE_CELL:
                counts.increasedPriceCells++; // there should only ever be one per row
                break;
              case CURRENT_PRICE_CELL:
                counts.currentPriceCells++;
                break;
            }
          });
        } else if (filterGroupId === CURRENT_MARKETSHARE_FILTER) {
          productFlags.forEach((flag) => {
            switch (flag) {
              case DECREASED_PRICE_CELL:
                counts.decreasedPriceCells = state.counts.decreasedPriceCells;
                break;
              case INCREASED_PRICE_CELL:
                counts.increasedPriceCells = state.counts.increasedPriceCells;
                break;
              case CURRENT_PRICE_CELL:
                counts.currentPriceCells = state.counts.currentPriceCells;
                break;
            }
          });
        }

        /*
         * PRODUCT_ALERT_FILTER
         */
        if (
          (product.isVisible && filterGroupId !== PRODUCT_ALERT_FILTER) ||
          singleFilterGroupIdSelected === PRODUCT_ALERT_FILTER
        ) {
          productFlags.forEach((flag) => {
            switch (flag) {
              case POSSIBLE_MISPRICED_PRODUCT:
                counts.mispricedProducts++;
                break;
              case UNPRICED_PRODUCT:
                counts.unpricedProducts++;
                break;
              case VALID_PRODUCT:
                counts.validProducts++;
                break;
            }
          });
        } else if (filterGroupId === PRODUCT_ALERT_FILTER) {
          productFlags.forEach((flag) => {
            switch (flag) {
              case POSSIBLE_MISPRICED_PRODUCT:
                counts.mispricedProducts = state.counts.mispricedProducts;
                break;
              case UNPRICED_PRODUCT:
                counts.unpricedProducts = state.counts.unpricedProducts;
                break;
              case VALID_PRODUCT:
                counts.validProducts = state.counts.validProducts;
                break;
            }
          });
        }

        /*
         * PRODUCTS_FILTER
         */
        if (
          (product.isVisible && filterGroupId !== PRODUCTS_FILTER) ||
          singleFilterGroupIdSelected === PRODUCTS_FILTER
        ) {
          productFlags.forEach((flag) => {
            switch (flag) {
              case INCUMBENT_PRODUCT:
                counts.incumbentProducts++;
                break;
              case NONINCUMBENT_PRODUCT:
                counts.nonincumbentProducts++;
                break;
            }
          });
        } else if (filterGroupId === PRODUCTS_FILTER) {
          productFlags.forEach((flag) => {
            switch (flag) {
              case INCUMBENT_PRODUCT:
                counts.incumbentProducts = state.counts.incumbentProducts;
                break;
              case NONINCUMBENT_PRODUCT:
                counts.nonincumbentProducts = state.counts.nonincumbentProducts;
                break;
            }
          });
        }

        /*
         * UNSPSCS_FILTER
         */
        if (
          product.isVisible ||
          singleFilterGroupIdSelected === UNSPSCS_FILTER
        ) {
          counts.productsByUnspsc[productUnspscId]++;
        } else if (
          filterGroupId === UNSPSCS_FILTER &&
          !includes(selectedUnspscFilterFlags, productUnspscId)
        ) {
          counts.productsByUnspsc[productUnspscId] =
            state.counts.productsByUnspsc[productUnspscId];
        }

        /*
         * FORMULARY_PRICE_Filter
         */
        if (productFlags.includes(DOES_NOT_MEET_FORMULARY_PRICING)) {
          counts.doesNotMeetFormularyPricing++;
        } else if (productFlags.includes(MEETS_FORMULARY_PRICING)) {
          counts.meetsFormularyPricing++;
        }

        /*
         * End All Filter Counts
         */
        if (product.isVisible) {
          counts.visibleProducts++;
          visibleUnspscs.add(masterProduct.unspsc.id);
        }

        // BUG-1286 to set the visibility
        this.cachedWijmoData[index].isVisible = product.isVisible;

        return counts;
      },
      initCounts,
    );

    counts.visibleUnspscs = visibleUnspscs.size;

    // BUG-1286
    this.cachedWijmoData.endUpdate();

    state.offers[this.currentOfferIndex(state)].offerPriced = offerPriced;
    state.offers[this.currentOfferIndex(state)].productPricingStatus =
      offerPriced ? 'COMPLETE' : 'NO_PRICING';

    state.counts.currentPriceCells = counts.currentPriceCells;
    state.counts.decreasedPriceCells = counts.decreasedPriceCells;
    state.counts.increasedPriceCells = counts.increasedPriceCells;
    state.counts.incumbentProducts = counts.incumbentProducts;
    state.counts.mispricedCells = counts.mispricedCells;
    state.counts.mispricedProducts = counts.mispricedProducts;
    state.counts.nonincumbentProducts = counts.nonincumbentProducts;
    state.counts.productsByUnspsc = counts.productsByUnspsc;
    state.counts.searchTermMatches = counts.searchTermMatches;
    state.counts.unpricedCells = counts.unpricedCells;
    state.counts.unpricedProducts = counts.unpricedProducts;
    state.counts.validProducts = counts.validProducts;
    state.counts.visibleProducts = counts.visibleProducts;
    state.counts.visibleUnspscs = counts.visibleUnspscs;
    state.counts.meetsFormularyPricing = counts.meetsFormularyPricing;
    state.counts.doesNotMeetFormularyPricing =
      counts.doesNotMeetFormularyPricing;
  }

  constructor(private api, private LoadingIndicator) {
    'ngInject';

    api.features.getFeatures().then((results) => {
      this.featureList = results;
    });

    // We need a default (empty) ObservableArray for our Wijmo cache.
    this.cachedWijmoData = new wijmo.collections.ObservableArray();

    // If you don't bind "this" on the reducer then when Redux calls it, this is bound to something completely
    // different.
    let boundReducer = this.offerPricingReducer.bind(this);

    // Create a Redux store to manipulate the state of this view: http://redux.js.org/index.html
    // Note: If we find the Redux DevTools (https://github.com/gaearon/redux-devtools) in the browser,
    // wire them up so we can easily debug this model.
    this.store = Redux.createStore(
      boundReducer,
      null,
      (window as Window).__REDUX_DEVTOOLS_EXTENSION__ &&
        (window as Window).__REDUX_DEVTOOLS_EXTENSION__(),
    );
  }

  // QA-1421 this will reset columns and rows after editing a tier
  resetMarketShare(state, offerTiers) {
    let highestTierUnderTargetMarketShare;
    let highestTierValue = -1;
    offerTiers.forEach((tier) => {
      if (
        tier.marketShare > highestTierValue &&
        this.currentOffer(state).currentMarketShare >= tier.marketShare
      ) {
        highestTierValue = tier.marketShare;
        highestTierUnderTargetMarketShare = tier.id;
      }
    });

    if (highestTierUnderTargetMarketShare) {
      state.offers[this.currentOfferIndex(state)].marketShareTier =
        highestTierUnderTargetMarketShare;
    }

    // Recalculate all the products because, the additional tiers need to have values.
    // Loop through all the changes in the change list. There might just be one product with one price, one
    // product with multiple prices (for example during the delete of all prices for a product), or multiple
    // price changes across multiple products.

    let newPrices = state.offers[this.currentOfferIndex(state)].prices.map(
      (productPricing) => {
        let offerForProduct = productPricing;
        let { adjustedPrices, flags, productFlags } = this._pricesAndFlags(
          offerForProduct,
          offerForProduct.pricePaid,
          offerTiers,
          state,
        );

        offerForProduct.adjustedPrices = adjustedPrices;
        offerForProduct.flags = flags;
        offerForProduct.productFlags = productFlags;

        return offerForProduct;
      },
    );

    // Put the new prices into the state.
    state.offers[this.currentOfferIndex(state)].prices = newPrices;

    this.updateWijmoProductData(state);
  }

  // Get various information about the model without having to dive into the Immutable.js structure directly from
  // code outside the model.
  isFetching() {
    return some(this.store.getState().fetching, (value) => value);
  }

  lowerBoundForTier(tierId) {
    let tiers = this.tiers();
    let index = findIndex(tiers, { id: tierId });

    if (index > 0) {
      return tiers[index - 1].marketShare + 1;
    } else {
      return 0;
    }
  }

  upperBoundForTier(tierId) {
    let tiers = this.tiers();
    let index = findIndex(tiers, { id: tierId });

    if (index < tiers.length - 1) {
      return tiers[index + 1].marketShare - 1;
    } else {
      return 90;
    }
  }

  validMarketShare(tierId, marketShare) {
    return (
      this.lowerBoundForTier(tierId) <= marketShare &&
      marketShare <= this.upperBoundForTier(tierId)
    );
  }

  getFilters(state: any = this.store.getState()) {
    return state.filters;
  }

  getSearchTermFilter(state: any) {
    return state.filters.searchTerm;
  }

  isComplete(offerId: number, state: any = this.store.getState()): boolean {
    return state.offers[this.findOfferIndex(offerId, state)].offerPriced;
  }

  productsCount() {
    return this.store.getState().counts.products;
  }

  unpricedCellsCount() {
    return this.store.getState().counts.unpricedCells;
  }

  mispricedCellsCount() {
    return this.store.getState().counts.mispricedCells;
  }

  increasedPriceCellsCount() {
    return this.store.getState().counts.increasedPriceCells;
  }

  decreasedPriceCellsCount() {
    return this.store.getState().counts.decreasedPriceCells;
  }

  currentPriceCellsCount() {
    return this.store.getState().counts.currentPriceCells;
  }

  unpricedProductsCount() {
    return this.store.getState().counts.unpricedProducts;
  }

  validProductsCount() {
    return this.store.getState().counts.validProducts;
  }

  mispricedProductsCount() {
    return this.store.getState().counts.mispricedProducts;
  }

  incumbentProductsCount() {
    return this.store.getState().counts.incumbentProducts;
  }

  nonincumbentProductsCount() {
    return this.store.getState().counts.nonincumbentProducts;
  }

  meetsFormularyPricingCount() {
    return this.store.getState().counts.meetsFormularyPricing;
  }

  doesNotMeetFormularyPricingCount() {
    return this.store.getState().counts.doesNotMeetFormularyPricing;
  }

  visibleProductsCount() {
    return this.store.getState().counts.visibleProducts;
  }

  visibleUnspscsCount() {
    return this.store.getState().counts.visibleUnspscs;
  }

  searchTermMatchesCount() {
    return this.store.getState().counts.searchTermMatches;
  }

  productsByUnspscCount() {
    return this.store.getState().counts.productsByUnspsc;
  }

  currentOffer(state: any = this.store.getState()) {
    return state.offers[this.currentOfferIndex(state)];
  }

  currentOfferIndex(state) {
    return this.findOfferIndex(this.offerId, state);
  }

  findOfferIndex(offerId: number, state: any) {
    return state.offers.findIndex((offer) => offer.id === offerId);
  }

  tiers(state = this.store.getState()) {
    let currentOffer = this.currentOffer(state);
    return currentOffer ? currentOffer.tiers : [];
  }

  product(productId: number, state: any) {
    return state.products[this.productIndex(productId, state)];
  }

  _allPricesAndFlags(productId: number, state: any = this.store.getState()) {
    if (this.currentOffer(state).pricingLoaded) {
      return state.offers[this.currentOfferIndex(state)].prices[
        this.productIndex(productId, state)
      ];
    } else {
      return null;
    }
  }

  uomAndUnitsInPackage(productId: number, state = this.store.getState()) {
    let product = this._allPricesAndFlags(productId, state);

    return {
      uom: product.uom,
      unitsInPackage: product.unitsInPackage,
    };
  }

  /**
   * Note: Used for unit testing the model.
   *
   * @param {number} productId
   * @param {number} tierId
   * @param state
   * @returns {any}
   */
  price(productId: number, tierId: number, state: any = this.store.getState()) {
    if (this.currentOffer(state).pricingLoaded) {
      return this.currentOffer(state).prices[
        this.productIndex(productId, state)
      ].prices[tierId.toString()];
    } else {
      return null;
    }
  }

  /**
   * Note: Used for unit testing the model.
   *
   * @param {number} productId
   * @param {number} tierId
   * @param state
   * @returns {any}
   */
  adjustedPrice(
    productId: number,
    tierId: number,
    state: any = this.store.getState(),
  ) {
    if (this.currentOffer(state).pricingLoaded) {
      return this.currentOffer(state).prices[
        this.productIndex(productId, state)
      ].adjustedPrices[tierId.toString()];
    } else {
      return null;
    }
  }

  /**
   * Note: Used for unit testing the model.
   *
   * @param {number} productId
   * @param {number} tierId
   * @param state
   * @returns {any}
   */
  flags(productId: number, tierId: number, state: any = this.store.getState()) {
    if (this.currentOffer(state).pricingLoaded) {
      return this.currentOffer(state).prices[
        this.productIndex(productId, state)
      ].flags[tierId.toString()];
    } else {
      return null;
    }
  }

  /**
   * Note: Used for unit testing the model.
   *
   * @param {number} productId
   * @param state
   * @returns {any}
   */
  productFlags(productId: number, state: any = this.store.getState()) {
    if (this.currentOffer(state).pricingLoaded) {
      return this.currentOffer(state).prices[
        this.productIndex(productId, state)
      ].productFlags;
    } else {
      return null;
    }
  }

  productIndex(productId: number, state: any) {
    return state.products.findIndex((product) => product.id === productId);
  }

  isMarketShare(tierId: number, state: any = this.store.getState()) {
    return tierId === this.currentOffer(state).marketShareTier;
  }

  getMarketShareTierId(tierId: number, state: any = this.store.getState()) {
    return this.currentOffer(state).marketShareTier;
  }

  updateWijmoProductData(state) {
    if (this.currentOffer(state).pricingLoaded) {
      this.cachedWijmoData.beginUpdate();

      // Combine pricing data with the Wijmo data (for example, after switching offers or when pricing first loads).
      state.products.forEach((product, index) => {
        let prices = this.currentOffer(state).prices[index];

        this.cachedWijmoData.setAt(
          index,
          assign({}, this.cachedWijmoData[index], product, {
            adjustedPrices: prices.adjustedPrices,
            flags: prices.flags,
            productFlags: prices.productFlags,
            crossed: product.numberOfCrossReferences > 0,
            uom: prices.uom,
            unitsInPackage: prices.unitsInPackage,
          }),
        );
      });

      this.cachedWijmoData.endUpdate();
    }
  }

  wijmoProductData() {
    return this.cachedWijmoData;
  }

  ////////////////////////////////////////////////////////////////////////////////
  // Actions - These are what outside code will call to trigger model changes.
  // Outside code does not dispatch actions directly to the Redux store, instead
  // it calls these functions or reads values from the state.
  ////////////////////////////////////////////////////////////////////////////////

  initialize(requestId: number, sellerId: number) {
    // I'm storing these off so they don't have to be passed in each and every time we call a function on the model.
    this.requestId = requestId;
    this.sellerId = sellerId;
  }

  setOffers(offers: any[]) {
    this.store.dispatch({ type: LOAD_OFFERS, data: offers });
  }

  // This code works with only one offer at a time. You select that offer here and until you change the offer, all
  // the rest of the actions below assume that is the offer to which they apply.
  selectOffer(offerId: number) {
    // I'm storing this off so it doesn't have to be passed in each and every time we call a function on the model.
    this.offerId = offerId;

    // TODO: This code now sucks. It was making separate calls to get product info and to get pricing. Now it's making
    // one call but loading each one separately from the same data. It needs to load all the data at once if we're
    // going to get it all from one call.
    let loadProducts = this.api.offers.getPricing(offerId);

    this.store.dispatch({ type: SELECT_OFFER, data: offerId });

    this.store.dispatch({ type: FETCH_PRODUCTS_AND_PRICING });

    return loadProducts.then((products) => {
      this.store.dispatch({ type: LOAD_PRODUCTS, data: products });
      this.store.dispatch({
        type: LOAD_PRICING,
        data: {
          offerId,
          pricing: products,
        },
      });
      this.store.dispatch({ type: UPDATE_COUNT });
    });
  }

  /**
   * A convenience function to set a single price for a single product without having to create a data structure to
   * do so.
   *
   * @param productId
   * @param tierId
   * @param newPrice
   */
  setPrice(productId: number, tierId: number, newPrice: number) {
    this.setPrices([
      {
        productId,
        prices: [
          {
            tierId,
            packagePrice: newPrice,
          },
        ],
      },
    ]);
  }

  /**
   * A convenience function to delete all pricing for a given product. Implemented using setPrices().
   *
   * @param productId
   */
  deletePrices(productId: number) {
    let product = {
      productId,
      prices: [],
    };

    product.prices = map(this.tiers(), (tier) => {
      return {
        tierId: tier.id,
        packagePrice: null,
      };
    });

    this.setPrices([product]);
  }

  /**
   * update the list of selected filters and then calculate counts
   */
  setFilters({ flags = [], searchTerm = '' } = {}) {
    this.store.dispatch({
      type: SET_FILTERS,
      data: {
        flags,
        searchTerm,
      },
    });
    this.store.dispatch({ type: UPDATE_COUNT });
  }

  resetFilters() {
    this.store.dispatch({
      type: RESET_FILTERS,
    });
  }

  updateCount() {
    this.store.dispatch({ type: UPDATE_COUNT });
  }

  setTier(tierId: number, marketShare: number) {
    // Validate the dollar market share before attempting to use it.
    if (!this.validMarketShare(tierId, marketShare)) {
      throw new Error(
        'marketShare is less than lower tier or greater than upper tier',
      );
    }

    this.store.dispatch({
      type: SET_TIER,
      data: {
        id: tierId,
        marketShare,
        name: `${marketShare}%`,
      },
    });

    this.store.dispatch({ type: UPDATE_COUNT });
  }

  insertTiers(insertableTiers: any) {
    // Make sure the right value is set for each tier. As with setting a tier above, we convert from a generic
    // "market share" to a specific kind of market share only when we're calling an API or receiving a value from an
    // API.
    let apiTiers = produce(insertableTiers, (draft) => {
      draft.forEach((tier) => {
        switch (this.currentOffer().spendLevelType.name) {
          case 'UNIT_MARKET_SHARE':
            tier.unitMarketShare = tier.marketShare;
            break;
          case 'DOLLAR_MARKET_SHARE':
          default:
            tier.dollarMarketShare = tier.marketShare;
            break;
        }

        delete tier.marketShare;
      });
    });

    return this.api.offers
      .insertTiers(this.offerId, apiTiers)
      .then((results) => {
        results.forEach((tier) => {
          tier.marketShare = tier.dollarMarketShare || tier.unitMarketShare;
        });

        // Dispatch an action to insert the new tiers in with all the others.
        this.store.dispatch({
          type: INSERT_TIERS,
          data: {
            tiers: results,
          },
        });
        this.store.dispatch({ type: UPDATE_COUNT });
      });
  }

  removeTier(tierId: number) {
    this.store.dispatch({
      type: REMOVE_TIER,
      data: {
        id: tierId,
      },
    });
    this.store.dispatch({ type: UPDATE_COUNT });
    return this.api.offers.removeTier(this.offerId, tierId);
  }

  setPackage(productId: number, uom: string, unitsInPackage: number) {
    this.store.dispatch({
      type: SET_PACKAGE,
      data: {
        productId,
        uom,
        unitsInPackage,
      },
    });
    this.store.dispatch({ type: UPDATE_COUNT });

    this.api.offers.updateProductsPricesCompressed(this.offerId, [
      {
        p: productId,
        uom,
        uc: unitsInPackage,
      },
    ]);
  }

  /**
   * This sets several prices for a single product or for multiple products at once.
   *
   * @param changeList - This is the same data structure passed to the API call
   * (Miredot Reference: https://integration.aptitude.com/miredot/ look for /offers/[offerId]/products PUT) to update
   * the offer's product pricing. It is an array of objects, each of which specifies a product and some number of
   * pricing changes at different tiers.
   */
  setPrices(changeList) {
    this.store.dispatch({
      type: SET_PRICES,
      data: changeList,
    });
    this.store.dispatch({ type: UPDATE_COUNT });

    // At this point the model is up-to-date so we can query it for all of the products which were in our change list
    // and make a new one.
    let apiCompatibleChangeList = map(changeList, (change: any) => {
      let { uom, unitsInPackage } = this.uomAndUnitsInPackage(change.productId);
      let combinedPrices = this.getPricesForApi(change.productId);

      return {
        p: change.productId,
        uom,
        uc: unitsInPackage,
        pcs: combinedPrices,
      };
    });

    let updateProductsLoader = this.api.offers.updateProductsPricesCompressed(
      this.offerId,
      apiCompatibleChangeList,
    );

    // Need to lock the UI during updates, also this will unlock the screen when we lock it in the deletePrices method
    this.LoadingIndicator.waitFor(updateProductsLoader);
  }

  private setPricesAfterImport(selectedOffer) {
    let apiCompatibleChangeList = map(selectedOffer.prices, (change: any) => {
      let { uom, unitsInPackage } = this.uomAndUnitsInPackage(change.id);
      let combinedPrices = this.getPricesForApi(change.id);

      return {
        p: change.id,
        uom,
        uc: unitsInPackage,
        pcs: combinedPrices,
      };
    });

    let updateProductsLoader = this.api.offers.updateProductsPricesCompressed(
      selectedOffer.id,
      apiCompatibleChangeList,
    );

    // Need to lock the UI during updates, also this will unlock the screen when we lock it in the deletePrices method
    this.LoadingIndicator.waitFor(updateProductsLoader);
  }

  private getPricesForApi(productId) {
    let allPricesAndFlags = this._allPricesAndFlags(productId);

    let adjustedPrices = mapValues(
      allPricesAndFlags.adjustedPrices,
      (value) => {
        return { packagePrice: value };
      },
    );

    let prices = mapValues(allPricesAndFlags.prices, (value) => {
      return { providedPackagePrice: value };
    });

    let combinedPrices = [];
    if (!this.isPriceAutoFillFlag) {
      const mergedPrices = merge(adjustedPrices, prices);
      let index = 0;
      let updatedKeyValue = {};
      combinedPrices = map(mergedPrices,
        (value: any, key: any) => {
          let packagePrice;
          if (!isNil(value.packagePrice)) {
            packagePrice = value.packagePrice;
          }
          updatedKeyValue[key] = packagePrice;
          index++;
          return {
            t: parseInt(key, 10),
            pp: packagePrice,
            ppp: value.providedPackagePrice,
          };
        },
      );
    } else {
      combinedPrices = map(
        merge(adjustedPrices, prices),
        (value: any, key: any) => {
          return {
            t: parseInt(key, 10),
            pp: value.packagePrice,
            ppp: value.providedPackagePrice,
          };
        },
      );
    }

    return combinedPrices;
  }

  acceptCorrectedPrice(productId: number, tierId: number) {
    let adjustedPrice = this.adjustedPrice(productId, tierId);

    // This is functionally identical to setting the price to the adjusted price so we just implement it that way.
    this.setPrice(productId, tierId, adjustedPrice);
  }
}

